import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Tag } from "lucide-react";
import { BLOG_POSTS } from "@/data/index";
import { springPresets } from "@/lib/motion";

/* @section: blog-hero */
function BlogHero() {
  return (
    <section className="py-28 bg-background border-b border-border">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="max-w-2xl"
        >
          <span className="text-xs text-primary tracking-[0.3em] uppercase mb-6 block">Column</span>
          <h1
            className="text-4xl md:text-5xl font-semibold text-foreground leading-[1.3] mb-6"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            コラム
          </h1>
          <div className="w-12 h-px bg-primary mb-6" />
          <p className="text-muted-foreground text-lg leading-[1.9]">
            組織行動論・リーダーシップ・AI×ビジネスをテーマに、
            現場で使える知見を発信しています。
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* @section: blog-list */
function BlogList() {
  const categories = ["すべて", ...Array.from(new Set(BLOG_POSTS.map((p) => p.category)))];
  const [activeCategory, setActiveCategory] = useState("すべて");

  const filtered = activeCategory === "すべて"
    ? BLOG_POSTS
    : BLOG_POSTS.filter((p) => p.category === activeCategory);

  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-6">
        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-1.5 text-xs tracking-wider rounded-sm border transition-colors ${
                activeCategory === cat
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-muted-foreground border-border hover:border-primary hover:text-primary"
              }`}
              style={{ letterSpacing: "0.08em" }}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Posts grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((post, i) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springPresets.gentle, delay: i * 0.07 }}
            >
            <Link
              to={`/blog/${post.id}`}
              className="border border-border rounded-sm p-7 bg-background hover:shadow-md transition-shadow group flex flex-col h-full cursor-pointer block"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="flex items-center gap-1.5 text-xs text-primary">
                  <Tag size={11} />
                  {post.category}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock size={11} />
                  {post.readTime}
                </span>
              </div>
              <h2
                className="text-base font-semibold text-foreground leading-[1.6] mb-3 group-hover:text-primary transition-colors flex-1"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {post.title}
              </h2>
              <p className="text-sm text-muted-foreground leading-[1.8] mb-5">
                {post.excerpt}
              </p>
              <div className="flex items-center justify-between mt-auto pt-4 border-t border-border">
                <span className="text-xs text-muted-foreground">
                  {post.date.replace(/-/g, ".")}
                </span>
                <span className="text-xs text-primary flex items-center gap-1 group-hover:gap-2 transition-all">
                  続きを読む <ArrowRight size={12} />
                </span>
              </div>
            </Link>
            </motion.article>
          ))}
        </div>

        {/* Coming soon note */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-14 text-sm text-muted-foreground"
        >
          <div className="inline-block border border-border rounded-sm px-6 py-3">
            随時更新中 — 新しいコラムをお楽しみに
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* @section: blog-cta */
function BlogCTA() {
  return (
    <section className="py-20 bg-muted/30 border-t border-border">
      <div className="container mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={springPresets.gentle}
        >
          <h2
            className="text-2xl font-semibold text-foreground mb-3"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            研修・コーチングについてのご相談
          </h2>
          <p className="text-muted-foreground mb-8">初回ご相談は無料です。</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground text-sm tracking-wider hover:opacity-90 transition-opacity rounded-sm"
            style={{ letterSpacing: "0.1em" }}
          >
            お問い合わせはこちら
            <ArrowRight size={15} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

export default function Blog() {
  return (
    <>
      <BlogHero />
      <BlogList />
      <BlogCTA />
    </>
  );
}
